import { useMemo, useState } from "react";

import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import { buildDayRows, DAYS_OF_WEEK } from "../sections/hours/hoursUtils";

function normalizeHours(hours = []) {
  return hours
    .map((hour) => ({
      id: hour?.id ?? null,
      startTime: hour?.startTime ?? "",
      endTime: hour?.endTime ?? "",
    }))
    .sort((left, right) => {
      const leftKey = `${left.id ?? "new"}-${left.startTime}-${left.endTime}`;
      const rightKey = `${right.id ?? "new"}-${right.startTime}-${right.endTime}`;
      return leftKey.localeCompare(rightKey);
    });
}

function areHoursEqual(leftHours = [], rightHours = []) {
  const normalizedLeft = normalizeHours(leftHours);
  const normalizedRight = normalizeHours(rightHours);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((hour, index) => {
    const compareHour = normalizedRight[index];
    return (
      hour.id === compareHour.id &&
      hour.startTime === compareHour.startTime &&
      hour.endTime === compareHour.endTime
    );
  });
}

export default function useProviderDetailHoursSection({
  permissionState,
  providerInformation,
  setProviderInformation,
}) {
  const providerRepository = useService("repositories/provider-repository");
  const officeHoursRepo = useService("repositories/provider-office-hours-repository");
  const providerId = providerInformation?.pk ?? providerInformation?.id;
  const dayRows = useMemo(() => buildDayRows(providerInformation), [providerInformation]);
  const [isSectionVisible, setIsSectionVisible] = useState(true);
  const [editingDays, setEditingDays] = useState(Array(DAYS_OF_WEEK.length).fill(false));
  const [draftHoursByDay, setDraftHoursByDay] = useState({});
  const [hoursToUpdate, setHoursToUpdate] = useState(new Map());
  const [hoursToDelete, setHoursToDelete] = useState(new Set());
  const hasUnsavedChangesByDay = useMemo(
    () =>
      DAYS_OF_WEEK.reduce((accumulator, _, dayIndex) => {
        const originalHours = dayRows[dayIndex]?.timings ?? [];
        const draftHours = draftHoursByDay[dayIndex] ?? originalHours;
        accumulator[dayIndex] = !areHoursEqual(originalHours, draftHours);
        return accumulator;
      }, {}),
    [dayRows, draftHoursByDay]
  );

  const startEditingDay = (dayIndex, existingHours) => {
    if (!permissionState.canEditHoursOfOperation && !permissionState.canAddHoursOfOperation) {
      return;
    }
    setEditingDays((prev) => prev.map((_, index) => index === dayIndex));
    setDraftHoursByDay((prev) => ({
      ...prev,
      [dayIndex]: existingHours.length ? existingHours.map((hour) => ({ ...hour })) : [{ startTime: "", endTime: "" }],
    }));
  };

  const stopEditingDay = (dayIndex) => {
    setEditingDays(Array(DAYS_OF_WEEK.length).fill(false));
    setDraftHoursByDay((prev) => {
      const nextValue = { ...prev };
      delete nextValue[dayIndex];
      return nextValue;
    });
    setHoursToUpdate(new Map());
    setHoursToDelete(new Set());
  };

  const updateHourField = (dayIndex, hourIndex, field, value) => {
    setDraftHoursByDay((prev) => {
      const updatedHours = (prev[dayIndex] || []).map((hour, index) =>
        index === hourIndex ? { ...hour, [field]: value } : hour
      );
      const updatedHour = updatedHours[hourIndex];
      if (updatedHour?.id) {
        setHoursToUpdate((prevUpdates) => {
          const nextUpdates = new Map(prevUpdates);
          nextUpdates.set(updatedHour.id, {
            id: updatedHour.id,
            startTime: updatedHour.startTime,
            endTime: updatedHour.endTime,
          });
          return nextUpdates;
        });
      }
      return { ...prev, [dayIndex]: updatedHours };
    });
  };

  const addHourWindow = (dayIndex) => {
    if (!permissionState.canAddHoursOfOperation) {
      return;
    }
    setDraftHoursByDay((prev) => ({
      ...prev,
      [dayIndex]: [...(prev[dayIndex] || []), { startTime: "", endTime: "" }],
    }));
  };

  const removeHourWindow = (dayIndex, hourIndex) => {
    const hour = draftHoursByDay?.[dayIndex]?.[hourIndex];
    if (hour?.id) {
      setHoursToDelete((prev) => new Set(prev).add(hour.id));
      setHoursToUpdate((prev) => {
        const nextUpdates = new Map(prev);
        nextUpdates.delete(hour.id);
        return nextUpdates;
      });
    }
    setDraftHoursByDay((prev) => ({
      ...prev,
      [dayIndex]: (prev[dayIndex] || []).filter((_, index) => index !== hourIndex),
    }));
  };

  const saveDayHours = async (dayIndex) => {
    try {
      const dayHours = draftHoursByDay[dayIndex] || [];
      const requests = [];
      const createPayload = dayHours.filter((hour) => !hour.id && hour.startTime && hour.endTime).map((hour) => ({
        provider: providerId,
        day: dayIndex,
        start_time: hour.startTime,
        end_time: hour.endTime,
      }));
      if (createPayload.length) {
        requests.push(officeHoursRepo.bulkCreate(createPayload));
      }
      if (hoursToUpdate.size) {
        requests.push(officeHoursRepo.bulkUpdate([...hoursToUpdate.values()]));
      }
      if (hoursToDelete.size) {
        requests.push(officeHoursRepo.bulkDelete([...hoursToDelete]));
      }
      if (requests.length) {
        await Promise.all(requests);
      }
      const refreshedProvider = await providerRepository.findRecord(providerId, { noCache: true });
      setProviderInformation((prev) => ({ ...prev, ...refreshedProvider }));
      stopEditingDay(dayIndex);
      getModalHandler().openSuccessModal();
    } catch (error) {
      getModalHandler().openFailureModal(error);
    }
  };

  return {
    dayRows,
    draftHoursByDay,
    hasUnsavedChangesByDay,
    editingDays,
    isSectionVisible,
    setIsSectionVisible,
    startEditingDay,
    stopEditingDay,
    updateHourField,
    addHourWindow,
    removeHourWindow,
    saveDayHours,
  };
}
